import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { FormLabel } from '../../../common/Atoms/FormLabel';
import { useGetSelectedOrganisation } from '../../hooks/useGetSelectedOrganisation';
import { useLazySearchContactsQuery } from '../../services/api/contactsApi/contacts';

export interface IPickedContact {
  id: number;
  name: string;
}

interface IContactSearchProps {
  label: string;
  selected: IPickedContact | undefined;
  onSelect: (contact: IPickedContact | undefined) => void;
  /** Contacts to leave out of the results */
  excludeIds?: number[];
  /** For toolbars: no autofocus, results float over the page, no "Selected" line */
  compact?: boolean;
}

/** Debounced type-ahead for picking a single contact in the current org */
export function ContactSearch({ label, selected, onSelect, excludeIds, compact }: IContactSearchProps) {
  const [query, setQuery] = useState(selected?.name || ``);
  const currentOrg = useGetSelectedOrganisation();
  const [searchContacts, { data: searchResults, isFetching }] = useLazySearchContactsQuery();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const results = useMemo(() => {
    if (!searchResults?.contacts) return [];

    return searchResults.contacts
      .filter(c => !excludeIds?.includes(c.id));
  }, [searchResults, excludeIds]);

  const handleQueryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    onSelect(undefined);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (value.length >= 1 && currentOrg?.id) {
      debounceRef.current = setTimeout(() => {
        searchContacts({
          organisationId: currentOrg.id,
          searchStr: value,
        });
      }, 300);
    }
  }, [currentOrg, searchContacts, onSelect]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  function handlePickContact(contact: IPickedContact) {
    onSelect(contact);
    setQuery(contact.name);
  }

  return (
    <>
      <div>
        <FormLabel>{ label }</FormLabel>
        <input
          type={ `text` }
          value={ query }
          onChange={ handleQueryChange }
          placeholder={ `Type to search contacts...` }
          className={ `mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-paidniceGreen-500 focus:ring-paidniceGreen-500 sm:text-sm` }
          autoFocus={ !compact }
        />
      </div>

      { query.length > 0 && !selected && (
        <div className={ `max-h-48 overflow-y-auto rounded-md border border-gray-200${compact ? ` absolute z-10 w-full bg-white shadow-lg` : ``}` }>
          { isFetching && (
            <p className={ `px-3 py-2 text-sm text-gray-400` }>{ `Searching...` }</p>
          ) }
          { !isFetching && results.length === 0 && (
            <p className={ `px-3 py-2 text-sm text-gray-400` }>{ `No contacts found` }</p>
          ) }
          { !isFetching && results.map(contact => contact.id ? (
            <button
              key={ contact.id }
              type={ `button` }
              onClick={ () => handlePickContact({ id: contact.id as number, name: contact.name }) }
              className={ `w-full cursor-pointer px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-hidden` }
            >
              { contact.name }
            </button>
          ) : null) }
        </div>
      ) }

      { selected && !compact && (
        <p className={ `text-sm text-gray-700` }>
          { `Selected: ` }
          <span className={ `font-medium` }>{ selected.name }</span>
        </p>
      ) }
    </>
  );
}
